import { MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	useAvailableCartridges,
	useBarrelLayout,
	useCommonStands,
	usePrepareRollForLine,
} from "../hooks/useRollsCartridge";

interface PrepareRollsetModalProps {
	rollsetId: number;
	/** The stand the rollset was built for — the legacy default. */
	defaultStandId: number | null;
	onClose: () => void;
	onSuccess: () => void;
	onError: (message: string) => void;
}

const NO_CARTRIDGE_VALUE = 0;

export const PrepareRollsetModal = ({
	rollsetId,
	defaultStandId,
	onClose,
	onSuccess,
	onError,
}: PrepareRollsetModalProps) => {
	const [standId, setStandId] = useState<number | null>(defaultStandId);
	const [cartridgeId, setCartridgeId] = useState<number>(NO_CARTRIDGE_VALUE);
	const [passPosition, setPassPosition] = useState<number | null>(null);

	const { data: stands = [] } = useCommonStands();
	const { data: barrelPasses = [], isLoading: isLoadingPasses } =
		useBarrelLayout(rollsetId);

	const selectedStand = useMemo(
		() => stands.find((s) => s.Id === standId) ?? null,
		[stands, standId],
	);

	const { data: cartridges = [] } = useAvailableCartridges(
		selectedStand?.MillSizeId ?? null,
	);

	// Deduplicate passes by PassPosition (SP may repeat per stand)
	const passes = useMemo(() => {
		const seen = new Map<number, (typeof barrelPasses)[0]>();
		for (const p of barrelPasses) {
			if (p.PassPosition != null && !seen.has(p.PassPosition)) {
				seen.set(p.PassPosition, p);
			}
		}
		return [...seen.values()].sort(
			(a, b) => (a.PassPosition ?? 0) - (b.PassPosition ?? 0),
		);
	}, [barrelPasses]);

	// Auto-select first good pass once data arrives (only if user hasn't picked one)
	useEffect(() => {
		if (passPosition == null && passes.length > 0) {
			const firstGood = passes.find(
				(p) =>
					p.PassStatusCode === "GOOD" ||
					p.PassStatusCode === "G" ||
					p.PassStatusCode === null,
			);
			if (firstGood?.PassPosition != null) {
				setPassPosition(firstGood.PassPosition);
			}
		}
	}, [passes, passPosition]);

	const { mutate, isPending } = usePrepareRollForLine({
		onSuccess: () => {
			onSuccess();
			onClose();
		},
		onError: (err) => onError(err.message),
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!standId || !passPosition) return;
		mutate({
			rollsetId,
			standId,
			cartridgeId: cartridgeId === NO_CARTRIDGE_VALUE ? null : cartridgeId,
			passPosition,
		});
	};

	const canSubmit = standId != null && passPosition != null && !isPending;

	return (
		<MesfModal
			id="modal-prepare-rollset"
			title="Prepare rollset for line"
			open={true}
			handleClose={onClose}
			maxWidth="sm"
		>
			<form onSubmit={handleSubmit}>
				<MesfModal.Content dividers style={{ padding: "20px 30px" }}>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<TextField
								select
								label="Stand"
								value={standId ?? ""}
								onChange={(e) => setStandId(Number(e.target.value))}
								fullWidth
								size="small"
							>
								{stands.map((s) => (
									<MenuItem key={s.Id} value={s.Id}>
										{s.Name}
										{s.MillSizeName ? ` — ${s.MillSizeName}` : ""}
									</MenuItem>
								))}
							</TextField>
						</Grid2>

						<Grid2 size={{ xs: 12 }}>
							<TextField
								select
								label="Cartridge"
								value={cartridgeId}
								onChange={(e) => setCartridgeId(Number(e.target.value))}
								fullWidth
								size="small"
								helperText={
									!selectedStand
										? "Pick a stand first to filter by mill size"
										: undefined
								}
							>
								<MenuItem value={NO_CARTRIDGE_VALUE}>
									<em>No cartridge</em>
								</MenuItem>
								{cartridges.map((c) => (
									<MenuItem key={c.Id} value={c.Id}>
										{c.Name}
										{c.MillSizeName ? ` — ${c.MillSizeName}` : ""}
									</MenuItem>
								))}
							</TextField>
						</Grid2>

						<Grid2 size={{ xs: 12 }}>
							<TextField
								select
								label="Starter Pass"
								value={passPosition ?? ""}
								onChange={(e) => setPassPosition(Number(e.target.value))}
								fullWidth
								size="small"
								disabled={isLoadingPasses || passes.length === 0}
								helperText={
									isLoadingPasses
										? "Loading passes…"
										: passes.length === 0
											? "No pass positions available for this rollset"
											: undefined
								}
							>
								{passes.map((p) => {
									const disabled = p.PassStatusCode === "NOTGOOD";
									const sched = (p.ScheduledTonnage ?? 0).toFixed(2);
									const rolled = (p.RolledTonnage ?? 0).toFixed(2);
									return (
										<MenuItem
											key={p.PassPosition}
											value={p.PassPosition ?? 0}
											disabled={disabled}
										>
											{p.PassPosition} - {p.PassName ?? "-"} - {sched}/{rolled}
										</MenuItem>
									);
								})}
							</TextField>
						</Grid2>
					</Grid2>

					{isLoadingPasses && (
						<Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
							<CircularProgress size={20} />
						</Box>
					)}
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "16px 30px 24px" }}>
					<FooterModalButtons
						isSubmitLoading={isPending}
						disabled={!canSubmit}
						onHide={() => onClose()}
						buttonLabel="Accept"
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
